import { useState, useEffect, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import HomeHeader from "../../components/header/HomeHeader";
import "./Home.css";
import { appApiHandler } from "../../api/appApiHandler";
import { getToken, getUserId } from "../../constants/asyncStoreManager";
import Banner from "./components/Banner";
import TitleHeader from "./components/TitleHeader";
import ProductCard from "./components/ProductCard";
import ShopByCategory from "./components/ShopByCategory";
import { setCategories } from "../../store/slices/categoryState";
import CommonFooter from "../../components/footer/CommonFooter";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import { colors } from "../../constants/colors";
import CommonButton from "../../components/button/CommonButton";
import { setNewMessage } from "../../store/slices/supportTicketState";
import WatchAndBuy from "./components/WatchAndBuy";
import NewArrivals from "./components/NewArrivals";
import FeaturedCollection from "./components/FeaturedCollection";
import { enrichProductsWithColors } from "../../utils/productInfo";
import { useNavigate } from "react-router-dom";
import BottomBar from "./components/BottomBar";
import SizeSlider from "./components/SizeSlider";

import ModelCollection from "./components/ModelCollection";
import OurIdentity from "./components/OurIdentity/Index";
import TestimonialSection from "./components/TestimonialSection/Index";
import ArtSection from "./components/ArtSection/Index";
import SocialShowcase from "./components/SocialShowcase";

const Home = () => {
  const LIMIT = 8;
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { categories } = useSelector((state) => state.categoryState);

  const bannerListRef = useRef(null);

  const [banners, setBanners] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [trendingProducts, setTrendingProducts] = useState([]);
  const [newArrivalsProducts, setNewArrivalsProducts] = useState([]);
  const [categoryWithProducts, setCategoryWithProducts] = useState([]);
  const [filterParams, setFilterParams] = useState([]);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  const [testimonial, setTestimonial] = useState([]);

  // Featured products pagination state
  const [featuredPage, setFeaturedPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const [totalFeaturedCount, setTotalFeaturedCount] = useState(0);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Update current index when user scrolls manually
  useEffect(() => {
    const scroller = bannerListRef.current;
    if (!scroller) return;

    const handleScroll = () => {
      const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
      setCurrentBannerIndex(currentIndex);
    };

    scroller.addEventListener("scroll", handleScroll);
    return () => scroller.removeEventListener("scroll", handleScroll);
  }, [windowWidth]);

  const BANNER_WIDTH = useMemo(() => {
    return Math.max(0, windowWidth);
  }, [windowWidth]);

  const getFilterParams = async () => {
    try {
      const response = await appApiHandler.getProductFilterParams();

      console.log("Filter Params =>", response);

      if (response?.status === 200) {
        setFilterParams(response?.sizes || []);
      }
    } catch (error) {
      console.log("Filter API Error =>", error);
    }
  };

  const getHomeData = async () => {
    setLoading(true);
    try {
      const homeRes = await appApiHandler.getHomeData({ limit: LIMIT });
      const categoryRes = await appApiHandler.getCategories();
      const getAllVideosRes = await appApiHandler.getAllVideos();
      //Testimonial
      const testimonialRes = await appApiHandler.getTestimonial();

      if (getAllVideosRes?.status == 200) {
        setVideos(getAllVideosRes?.data || []);
      }

      if (categoryRes?.status == 200) {
        dispatch(setCategories(categoryRes?.data || []));
      }

      if (homeRes?.status == 200) {
        setBanners(homeRes?.banners || []);

        const enrich = (data, setter) => {
          setter(data);
          enrichProductsWithColors(data, {
            concurrency: 1,
            interChunkDelayMs: 380,
          }).then((enriched) => setter(enriched));
        };

        if (homeRes.featured_products?.length > 0) {
          enrich(homeRes.featured_products, setFeaturedProducts);
          setTotalFeaturedCount(
            homeRes.featured_products.length >= 8
              ? homeRes.featured_products.length + 1
              : homeRes.featured_products.length,
          );
        }

        if (homeRes.trending_products?.length > 0) {
          enrich(homeRes.trending_products, setTrendingProducts);
        }

        if (homeRes.new_arrivals_products?.length > 0) {
          enrich(homeRes.new_arrivals_products, setNewArrivalsProducts);
        }

        if (homeRes.categories?.length > 0) {
          enrich(homeRes.categories, setCategoryWithProducts);
        }

        //testimonial
        if (testimonialRes?.status === 200 || testimonialRes?.status === 1) {
          const list =
            testimonialRes?.data?.testimonials ||
            testimonialRes?.data?.data ||
            testimonialRes?.data ||
            [];

          setTestimonial(Array.isArray(list) ? list : []);
        }
      }

      const isLoggedIn = await getToken();
      if (isLoggedIn) {
        const user_id = await getUserId();
        const ticketResponse = await appApiHandler.getSupportTickets({
          user_id,
        });
        if (ticketResponse?.status === 200 && ticketResponse?.data) {
          const hasUnread =
            Array.isArray(ticketResponse.data) &&
            ticketResponse.data.some((t) => Number(t?.unread_count) > 0);
          dispatch(setNewMessage(hasUnread ? 1 : 0));
        }
      }
    } catch (e) {
      console.error("getHomeData", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getHomeData();
    getFilterParams();
  }, []);

  // Auto-advance banner with scrollTo
  useEffect(() => {
    if (!bannerListRef.current || banners.length === 0 || windowWidth === 0)
      return;
    const scroller = bannerListRef.current;
    const intervalId = setInterval(() => {
      const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
      const nextIndex = (currentIndex + 1) % banners.length;
      scroller.scrollTo({ left: nextIndex * windowWidth, behavior: "smooth" });
      setCurrentBannerIndex(nextIndex);
    }, 5000);
    return () => clearInterval(intervalId);
  }, [banners, windowWidth]);

  // Navigation functions with scrollTo
  const goToNextBanner = () => {
    if (!bannerListRef.current || banners.length === 0) return;
    const scroller = bannerListRef.current;
    const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
    const nextIndex = (currentIndex + 1) % banners.length;
    scroller.scrollTo({ left: nextIndex * windowWidth, behavior: "smooth" });
    setCurrentBannerIndex(nextIndex);
  };

  const goToPreviousBanner = () => {
    if (!bannerListRef.current || banners.length === 0) return;
    const scroller = bannerListRef.current;
    const currentIndex = Math.round(scroller.scrollLeft / windowWidth);
    const prevIndex =
      currentIndex === 0 ? banners.length - 1 : currentIndex - 1;
    scroller.scrollTo({ left: prevIndex * windowWidth, behavior: "smooth" });
    setCurrentBannerIndex(prevIndex);
  };

  const goToBanner = (index) => {
    if (!bannerListRef.current || banners.length === 0) return;
    const scroller = bannerListRef.current;
    scroller.scrollTo({ left: index * windowWidth, behavior: "smooth" });
    setCurrentBannerIndex(index);
  };

  // Load more featured products
  const loadMoreFeaturedProducts = async () => {
    if (loadingMore || featuredProducts.length >= totalFeaturedCount) return;

    setLoadingMore(true);
    const nextPage = featuredPage + 1;
    const featuredReqData = {
      category_id: "featured",
      page: nextPage,
      limit: 8,
    };

    try {
      const response = await appApiHandler.getProducts(featuredReqData);
      if (response?.status === 200 && response?.data) {
        setFeaturedProducts((prev) => [...prev, ...response.data]);
        setFeaturedPage(nextPage);
        // if returned less than 8, no more pages
        if (response.data.length < 8) {
          setTotalFeaturedCount((prev) => prev - 1); // collapse the +1 sentinel
        }
      }
    } catch (error) {
      console.error("Error loading more featured products:", error);
    } finally {
      setLoadingMore(false);
    }
  };
  //console.log(featuredProducts.length, LIMIT, newArrivalsProducts.length);

  return (
    <div className="home-container">
      {/* <HomeHeader /> */}
      <main className="home-main">
        {banners?.length > 0 && (
          <section className="home-banners">
            <div className="home-banners__container">
              {/* Scrollable Banner Container */}
              <div
                className="home-banners__scroller"
                ref={bannerListRef}
                style={{ gridAutoColumns: `${windowWidth}px` }}
              >
                {banners.map((item, index) => (
                  <div key={item?._id} className="home-banners__item">
                    <Banner
                      item={item}
                      isActive={index === currentBannerIndex}
                    />
                  </div>
                ))}
              </div>

              {/* Navigation Arrows */}
              {banners.length > 1 && (
                <>
                  <button
                    className="home-banners__nav home-banners__nav--prev"
                    onClick={goToPreviousBanner}
                  >
                    <FaChevronLeft color={colors.primary} />
                  </button>
                  <button
                    className="home-banners__nav home-banners__nav--next"
                    onClick={goToNextBanner}
                  >
                    <FaChevronRight color={colors.primary} />
                  </button>
                </>
              )}

              {/* Dots Navigation */}
              {banners.length > 1 && (
                <div className="home-banners__dots">
                  {banners.map((_, index) => (
                    <button
                      key={index}
                      className={`home-banners__dot ${
                        index === currentBannerIndex
                          ? "home-banners__dot--active"
                          : ""
                      }`}
                      onClick={() => goToBanner(index)}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>
        )}
        <BottomBar />
        {categories?.length > 0 && <ShopByCategory categories={categories} />}

        {featuredProducts?.length > 0 && (
          <NewArrivals
            title="Featured Products"
            subtitle="Hot Selling Designer Blouse"
            products={featuredProducts || []}
            onPress={() => {
              navigate(`/category?category_id=featured`, {
                state: {
                  category: {
                    _id: "featured",
                    name: "Featured Products",
                    image: featuredProducts[0]?.image?.[0]?.image,
                  },
                },
              });
            }}
            linkState={{ listingView: "featured" }}
            sectionClassName="new-arrivals new-arrivals--trending"
            stripScrollSingleStep
            showViewAll={featuredProducts?.length >= LIMIT}
          />
        )}

        {trendingProducts?.length > 0 && (
          <NewArrivals
            title="Trending"
            subtitle="Top view in this week"
            products={trendingProducts || []}
            onPress={() => {
              navigate(`/category?category_id=trending`, {
                state: {
                  category: {
                    _id: "trending",
                    name: "Trending",
                    image: trendingProducts[0]?.image?.[0]?.image,
                  },
                },
              });
            }}
            linkState={{ listingView: "trending" }}
            sectionClassName="new-arrivals new-arrivals--trending"
            stripScrollSingleStep
            showViewAll={trendingProducts?.length >= LIMIT}
          />
        )}

        {videos?.length > 0 && <WatchAndBuy videos={videos} />}

        {newArrivalsProducts?.length > 0 && (
          <NewArrivals
            title="New Arrivals"
            subtitle="Just landed in our store"
            products={newArrivalsProducts}
            onPress={() => {
              navigate(`/category?category_id=new_arrivals`, {
                state: {
                  category: {
                    _id: "new_arrivals",
                    name: "New Arrivals",
                    image: newArrivalsProducts[0]?.image?.[0]?.image,
                  },
                },
              });
            }}
            linkState={{ listingView: "new_arrivals" }}
            showViewAll={newArrivalsProducts?.length >= LIMIT}
          />
        )}

        <SizeSlider
          sizes={filterParams || []}
          onSizeClick={(size) => {
            console.log("CLICK SIZE =>", size);

            navigate(`/category?category_id=new_arrivals`, {
              state: {
                selectedSize: size,
              },
            });
          }}
        />

        <ModelCollection />

        <SocialShowcase />

        {(categoryWithProducts || []).map((cat) => (
          <NewArrivals
            key={`cat-strip-${cat?._id}`}
            title={cat?.name}
            subtitle={`Discover our ${cat?.name} collection`}
            products={cat?.products || []}
            onPress={() => {
              navigate(`/category?category_id=${cat?._id}`, {
                state: { category: cat },
              });
            }}
            showViewAll={cat?.products?.length >= LIMIT}
          />
        ))}

        <OurIdentity />

        {testimonial?.length > 0 && (
          <TestimonialSection testimonials={testimonial} />
        )}
      

        <ArtSection />
      </main>
      <CommonFooter />
    </div>
  );
};

export default Home;
